/**
 * Contains the types used internally by `TmTable` and `TableViewManager`.
 *
 * Do not export these to consumers in the barrel file. They are intended to
 * be used only within the implementation of the table and its supporting classes
 * and don't represent contracts for external consumers.
 */

import { Action } from '@jack-henry/frontend-utils/types';
import { DeepRequired, OmitDeepRequired } from '@treasury/utils/types';
import { TemplateResult } from 'lit';
import {
    AllViewId,
    NullViewId,
    SingleViewId,
    TmTableAction,
    TmTableConfig,
    TmTableConfigMulti,
    TmTableFilter,
    TmTableRowAction,
    TmTableSelectAction,
    TmTableSelectCustomDef,
    TmTableView,
} from './tm-table.types';

/**
 * The set of keys used by `EnrichedTableItem<T>`, which are
 * in turn relied on by `<jha-advanced-table>` for specific purposes.
 *
 * These keys are prefixed to avoid collisions with properties on the original item.
 * They are defined as an enum to allow for easier cross-reference and
 * to enable updating in a single place.
 */
export enum EnrichedTableItemKey {
    TableId = 'tmTableId',
    IsActive = 'tmTableIsActive',
}
/**
 * A table item enriched with additional metadata used by the `<jha-advanced-table>`.
 */
export type EnrichedTableItem<T> = T & {
    /**
     * The ID used to associate an item provided by a consumer with
     * the item itself during events like multi-selection.
     */
    [EnrichedTableItemKey.TableId]: string;
    /**
     * If `true`, this item's row will appear as active (highlighted) in the table.
     */
    [EnrichedTableItemKey.IsActive]: boolean;
};

export interface TmTableActionInternal<T> extends Omit<TmTableAction<T>, 'execute'> {
    execute: Action | (() => Promise<void>);
}

/**
 * Augments the `TmTableFilter` shape with additional data not specified by consumers.
 */
export interface TmTableFilterInternal<T> extends TmTableFilter<T> {
    /**
     * The menu items to be displayed in the filter drop down.
     */
    choices: string[];
}

/**
 * Augments `TmTableView` with additional metadata not required
 * from external consumers or required in a different format.
 */
export interface TmTableViewInternal<T> extends TmTableViewNormalized<T> {
    viewId: string | typeof AllViewId | typeof NullViewId | typeof SingleViewId;
    label: string;
}

/**
 * Multi-select action representation used internally.
 *
 * The parameters of the consumer-facing version are filled in
 * by `TableViewManager` during enrichment.
 */
export interface TmTableSelectActionInternal<T> extends Omit<TmTableSelectAction<T>, 'execute'> {
    execute: () => Promise<void>;
}

/**
 * Multi-select custom definition used internally.
 *
 * The parameters of the consumer-facing version are filled in
 * by `TableViewManager` during enrichment.
 */
export interface TmTableSelectCustomDefInternal<T> extends Omit<
    TmTableSelectCustomDef<T>,
    'render'
> {
    render: (close: Action) => string | TemplateResult;
}

export interface TmTableRowActionInternal<T> extends TmTableRowAction<T> {
    actionId: string;
}

/**
 * Represents the full set of configuration options used by `TmTable`
 * where optional properties have been given defaults.
 *
 * While some optional properties that cannot have a sensible default value remain,
 * this presents a more strict contract to ensure default behavior.
 */
export type TmTableOptionsNormalized<T = unknown> =
    | TmTableConfigNormalized<T>
    | TmTableConfigMultiNormalized<T>;

export type TmTableConfigNormalized<T = unknown> = OmitDeepRequired<
    TmTableConfig<T>,
    'initialActive'
>;

export type TmTableConfigMultiNormalized<T = unknown> = DeepRequired<
    Omit<TmTableConfigMulti<T>, 'views'>
> & {
    views: TmTableViewNormalized<T>[];
};

export type TmTableViewNormalized<T = unknown> = OmitDeepRequired<TmTableView<T>, 'initialActive'>;
